import { PageComponent } from "barba";
import GoogleMapsRecycler from "utils/google-maps-recycler";
import ShadesOfGreyStyles from "utils/google-maps-styles/shades-of-grey";
import PinIcon from "utils/google-maps-icons/pin";
import scrollToNode from "utils/scroll-to-node";

/*

    Attributes:
        data-dealers-map: marks the element that contains both the map and the list of locations
        data-map: marks the element where the map will be injected. Value should be the API Key to use.
        data-dealer: marks a single dealer element. Should also have these attributes:
            data-dealer-lat: latitude
            data-dealer-lng: longitude

            Should also contain child elements with these attributes:
                data-dealer-num: marks the element containing the label to put on the marker
                data-dealer-name: marks the element containing the title text to put on the marker

*/

class DealersMap {
    constructor({ node }) {
        this.mapElement = node.querySelector("[data-map]");
        this.apiKey = this.mapElement.dataset.map;
        this.dealerElements = node.querySelectorAll("[data-dealer]");
        this.map = null;
        this.markers = [];

        this.initialize();
    }

    async initialize() {
        await this.createMap();
        this.createAllMarkers();
        this.fitMapToMarkers();
    }

    async createMap() {
        this.map = await GoogleMapsRecycler.attachMap(this.mapElement, this.apiKey);

        this.map.setOptions({
            disableDefaultUI: true,
            styles: ShadesOfGreyStyles
        });
    }

    createAllMarkers() {
        for (const dealer of this.dealerElements) {
            const id = dealer.getAttribute("id");
            const num = dealer.querySelector("[data-dealer-num]").dataset.dealerNum;
            const name = dealer.querySelector("[data-dealer-name]").innerText;
            const lat = parseFloat(dealer.dataset.dealerLat);
            const lng = parseFloat(dealer.dataset.dealerLng);

            this.markers.push(this.createMarker(lat, lng, num, name, id));
        }
    }

    createMarker(lat, lng, number, name, targetId) {
        const marker = new google.maps.Marker({
            map: this.map,
            position: new google.maps.LatLng(lat, lng),
            clickable: true,
            title: name,
            label: {
                color: "#252523",
                fontFamily: "Montserrat, Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: "700",
                text: number
            },
            icon: {
                ...PinIcon,
                labelOrigin: new google.maps.Point(0, -20)
            }
        });

        marker.addListener("click", () => this.scrollToTarget(targetId));

        return marker;
    }

    deleteAllMarkers() {
        this.markers.forEach((x) => this.deleteMarker(x));
    }

    deleteMarker(marker) {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
    }

    fitMapToMarkers() {
        const bounds = new google.maps.LatLngBounds();
        for (const marker of this.markers) {
            bounds.extend(marker.getPosition());
        }

        this.map.fitBounds(bounds, 20);
    }

    scrollToTarget(targetId) {
        const node = document.querySelector(`#${targetId}`);
        if (node) {
            window.requestAnimationFrame(() => scrollToNode(node));
        }
    }

    destroy() {
        delete this.mapElement;
        delete this.apiKey;
        delete this.dealerElements;

        this.deleteAllMarkers();
        delete this.markers;

        if (this.map) {
            GoogleMapsRecycler.detachMap(this.map);
        }

        delete this.map;
    }
}

new PageComponent("[data-dealers-map]", DealersMap);
